import React, { useState, useCallback } from 'react';
import { Text, Pressable, View } from 'react-native';
import PropTypes from 'prop-types';
import { useStore } from 'Store';
import { t } from 'Locale';
import { isContentBlocked, isContentLocked } from 'Utils/parentalControlUtils';
import Icon from '../Icon';
import Checkbox from '../Checkbox';
import styles from './RecordingGroup.jss';
import { CachedImage } from '../CachedImage';

const RecordingItem = ({
    iconUrl,
    title,
    subTitle,
    updateList,
    children,
    isEditing,
    isSelected,
    isFirst,
    onCheckbox,
    IPTVonly,
    parentalRating,
    adult
}) => {
    const { parentalLockConfig } = useStore();
    const [open, setOpen] = useState(false);

    const toggleExpanded = useCallback(() => {
        setOpen(!open);
        updateList();
    }, [open, updateList, children]);

    const adultLock = isContentLocked(adult, parentalLockConfig);
    const ageBlock = isContentBlocked(parentalRating, parentalLockConfig);
    const groupMetaContainerStyle = open
        ? [styles.groupMetaContainer, styles.groupMetaContainerOpen]
        : styles.groupMetaContainer;
    return (
        <View style={[open && styles.openRow]}>
            <Pressable style={groupMetaContainerStyle} onPress={toggleExpanded}>
                {ageBlock || adultLock ? (
                    <Icon
                        name="lock"
                        style={[styles.lockIcon, isEditing && styles.hiddenIconStyle]}
                    />
                ) : (
                    <CachedImage
                        source={{ uri: iconUrl }}
                        style={[styles.icon, isEditing && styles.hiddenIconStyle]}
                        resizeMode="contain"
                    />
                )}
                <View style={[styles.textArea, isFirst && styles.firstTextArea]}>
                    <Icon
                        style={[styles.dropDown, !open && styles.dropdownArrowDownStyle]}
                        name="dropdown"
                    />
                    <View style={styles.metadata}>
                        <Text numberOfLines={1} style={styles.programTitle}>
                            {adultLock ? t('RECORDINGS.LOCKED') : title}
                        </Text>
                        <Text style={styles.programSubtitle}>{subTitle}</Text>
                        {IPTVonly && (
                            <View style={styles.iptvOnly}>
                                <Icon name="info" style={styles.iptvInfoIcon} />
                                <Text style={styles.iptvOnlyText}>
                                    {t('RECORDINGS.IPTV_RECORDINGS')}
                                </Text>
                            </View>
                        )}
                    </View>
                    {isEditing && (
                        <Checkbox
                            key={isSelected ? 'selected' : 'empty'}
                            style={styles.checkbox}
                            checked={isSelected}
                            onPress={onCheckbox}
                            hitSlop={{ top: 8, left: 8, right: 8, bottom: 8 }}
                        />
                    )}
                </View>
            </Pressable>
            <View style={{ height: open ? 'auto' : 0, overflow: 'hidden' }}>{children}</View>
        </View>
    );
};

RecordingItem.defaultProps = {
    iconUrl: '',
    title: '',
    subTitle: '',
    children: null,
    isSelected: false,
    isEditing: false,
    isFirst: false,
    IPTVonly: false,
    parentalRating: null,
    adult: false,
    onCheckbox: () => {}
};

RecordingItem.propTypes = {
    iconUrl: PropTypes.string,
    title: PropTypes.string,
    subTitle: PropTypes.string,
    children: PropTypes.node,
    isSelected: PropTypes.bool,
    isEditing: PropTypes.bool,
    isFirst: PropTypes.bool,
    IPTVonly: PropTypes.bool,
    parentalRating: PropTypes.string,
    adult: PropTypes.bool,
    onCheckbox: PropTypes.func,
    updateList: PropTypes.func.isRequired
};

export default RecordingItem;
